import { z } from 'zod'
import { SIGNUP_COUNTRIES } from '../validation/age'
import { normalizeBio, PROFILE_BIO_MAX_LENGTH, PROFILE_RING_COLORS } from '../validation/profile'
import { instagramHandle, linkedinUrl } from '../validation/profile-links'

/** Who finds the person by name: anyone, only people they know, or nobody (link or QR only). */
export const findableBySchema = z.enum(['everyone', 'connections', 'nobody'])
export type FindableBy = z.infer<typeof findableBySchema>

export const userProfileSchema = z.object({
  id: z.string().uuid(),
  authUserId: z.string().uuid(),
  displayName: z.string(),
  username: z.string().nullable(),
  /** How often the handle was replaced after it was first set. */
  usernameChanges: z.number().int(),
  dateOfBirth: z.string().nullable(),
  /** When the date of birth was first set; the edit window counts from here. */
  dateOfBirthSetAt: z.string().nullable(),
  /** Corrections made since the date of birth was first set. */
  dateOfBirthChanges: z.number().int(),
  /** Where the person lives (ISO 3166-1 alpha-2); sets the minimum age. */
  country: z.string().nullable(),
  /** Said they study; student events rank first in their feed. */
  isStudent: z.boolean(),
  gender: z.enum(['unknown', 'male', 'female', 'diverse']),
  homeTown: z.string(),
  avatarUrl: z.string().nullable(),
  roles: z.array(z.string()),
  isDiscoverable: z.boolean(),
  findableBy: findableBySchema,
  assistantEnabled: z.boolean(),
  /** Friends see day and month of the date of birth. */
  birthdayVisible: z.boolean(),
  /** Anyone on an event page sees that this person goes or is interested. */
  attendancePublic: z.boolean(),
  /** Others in a Live Room see the age next to the name. */
  roomAgeVisible: z.boolean(),
  instagram: z.string().nullable(),
  linkedin: z.string().nullable(),
  bio: z.string().nullable(),
  pronouns: z.string().nullable().optional(),
  /** The colour of the ring around the profile picture; null is automatic. */
  ringColor: z.enum(PROFILE_RING_COLORS).nullable().optional(),
  isOfficial: z.boolean(),
  phoneHash: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
})

export const userProfilePreferencesSchema = z.object({
  id: z.string().uuid(),
  userProfileId: z.string().uuid(),
  clientPreferences: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const userAuthMethodsResponseSchema = z.object({
  hasPassword: z.boolean(),
  providers: z.array(z.string()),
})

function profileLink(normalize: (input: string) => string | null) {
  return z
    .string()
    .trim()
    .max(200)
    .nullable()
    .transform((value, ctx) => {
      if (value === null || value === '') return null
      const link = normalize(value)
      if (!link) {
        ctx.addIssue({ code: 'custom', message: 'Not a profile link' })
        return z.NEVER
      }
      return link
    })
    .optional()
}

export const updateProfileRequestSchema = z.object({
  displayName: z.string().min(1).max(100).optional(),
  /** `YYYY-MM-DD`; refused below the minimum age and once the edit window is spent. */
  dateOfBirth: z.string().date().optional(),
  /** Where the person lives; the minimum age follows it. */
  country: z.enum(SIGNUP_COUNTRIES).optional(),
  /** "Ich studiere": student events rank first in the feed. */
  isStudent: z.boolean().optional(),
  gender: z.enum(['unknown', 'male', 'female', 'diverse']).optional(),
  homeTown: z.string().max(200).optional(),
  /** Account-wide assistant opt-out; the plan assistant never reads a chat while this is false. */
  assistantEnabled: z.boolean().optional(),
  birthdayVisible: z.boolean().optional(),
  attendancePublic: z.boolean().optional(),
  roomAgeVisible: z.boolean().optional(),
  findableBy: findableBySchema.optional(),
  /** An Instagram handle or profile link; empty clears it. */
  instagram: profileLink(instagramHandle),
  /** A LinkedIn profile link or its name part; empty clears it. */
  linkedin: profileLink(linkedinUrl),
  /** The ring around the profile picture; null goes back to automatic. */
  ringColor: z.enum(PROFILE_RING_COLORS).nullable().optional(),
  /** How the person wants to be referred to; trimmed, empty clears it. */
  pronouns: z
    .string()
    .trim()
    .max(30)
    .nullable()
    .transform((value) => value || null)
    .optional(),
  /** "Über mich"; trimmed, empty clears it. */
  bio: z
    .string()
    .max(PROFILE_BIO_MAX_LENGTH * 4)
    .nullable()
    .transform((value, ctx) => {
      const bio = normalizeBio(value)
      if (bio === undefined) {
        ctx.addIssue({ code: 'custom', message: `At most ${PROFILE_BIO_MAX_LENGTH} characters` })
        return z.NEVER
      }
      return bio
    })
    .optional(),
})

export const setUsernameRequestSchema = z.object({
  username: z.string(),
})

export const setPhoneRequestSchema = z.object({
  /** Raw phone input; null clears the number. Normalized to E.164 server-side. */
  phone: z.string().max(32).nullable(),
  /** ISO 3166-1 alpha-2 region hint for nationally-formatted numbers. */
  country: z.string().length(2).optional(),
})

export const usernameAvailabilityResponseSchema = z.object({
  /** Lowercased/trimmed candidate. */
  normalized: z.string(),
  /** Passes the format rules (length + allowed chars). */
  valid: z.boolean(),
  /** Not taken by another user (only meaningful when valid). */
  available: z.boolean(),
})

export const usernameSuggestionsResponseSchema = z.object({
  suggestions: z.array(z.string()),
})

export const userPreferencesResponseSchema = z.object({
  tags: z.array(z.string()),
  client: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
})

// /me returns the profile with preferences inlined so a single request covers
// the post-login routing decision (onboardingComplete lives in client prefs).
/** Profile parts an account still lacks, for the short "Fast fertig" screen after a party night. */
export const missingProfileFieldValues = ['username', 'dateOfBirth'] as const
export type MissingProfileField = (typeof missingProfileFieldValues)[number]

export const userProfileWithPreferencesSchema = userProfileSchema.extend({
  preferences: userPreferencesResponseSchema,
  /**
   * What the account still has to fill in; empty once complete. Always empty for web guests, who
   * have no profile to complete.
   */
  missingProfileFields: z.array(z.enum(missingProfileFieldValues)).default([]),
})

export const clientPreferenceRequestSchema = z.object({
  key: z.string().min(1),
  value: z.unknown(),
})

export const deleteClientPreferenceQuerySchema = z.object({
  key: z.string().min(1),
})

export const onboardingTagsRequestSchema = z.object({
  tagIds: z.array(z.string().uuid()),
})

export const deleteAccountRequestSchema = z.object({
  currentPassword: z.string().optional(),
  typedConfirmation: z.string().optional(),
})

export const deleteAccountResponseSchema = z.object({
  deleted: z.boolean(),
})

/** POST /me/absorb-guest — the web guest session the caller was using before signing in. */
export const absorbGuestRequestSchema = z.object({
  guestAccessToken: z.string().min(1).max(8192),
})

/** How many of the guest's rows now belong to the caller, by kind. */
export const absorbedGuestCountsSchema = z.object({
  plans: z.number().int(),
  participations: z.number().int(),
  invites: z.number().int(),
  chats: z.number().int(),
  messages: z.number().int(),
  reactions: z.number().int(),
  votes: z.number().int(),
  ballots: z.number().int(),
  listItems: z.number().int(),
  listClaims: z.number().int(),
  spots: z.number().int(),
  friendships: z.number().int(),
  savedEvents: z.number().int(),
})

/** `absorbed` is false when the guest was already gone, e.g. on a repeated call. */
export const absorbGuestResponseSchema = z.object({
  absorbed: z.boolean(),
  moved: absorbedGuestCountsSchema,
})

export type UserProfileDto = z.infer<typeof userProfileSchema>
export type UserProfilePreferencesDto = z.infer<typeof userProfilePreferencesSchema>
export type UserAuthMethodsResponse = z.infer<typeof userAuthMethodsResponseSchema>
export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>
export type SetUsernameRequest = z.infer<typeof setUsernameRequestSchema>
export type UsernameAvailabilityResponse = z.infer<typeof usernameAvailabilityResponseSchema>
export type UsernameSuggestionsResponse = z.infer<typeof usernameSuggestionsResponseSchema>
export type UserPreferencesResponse = z.infer<typeof userPreferencesResponseSchema>
export type ClientPreferenceRequest = z.infer<typeof clientPreferenceRequestSchema>
export type OnboardingTagsRequest = z.infer<typeof onboardingTagsRequestSchema>
export type DeleteAccountRequest = z.infer<typeof deleteAccountRequestSchema>
export type DeleteAccountResponse = z.infer<typeof deleteAccountResponseSchema>
export type AbsorbGuestRequest = z.infer<typeof absorbGuestRequestSchema>
export type AbsorbedGuestCounts = z.infer<typeof absorbedGuestCountsSchema>
export type AbsorbGuestResponse = z.infer<typeof absorbGuestResponseSchema>
